import { useEffect, useState } from "react";
import Styles from "./index.module.scss";
import Tooltip from "../../elements/tooltip";
import PasswordPolicy from "../../elements/passwordPolicy";
import Button from "../../elements/button";
import TextBox from "../../elements/textbox";
import useWindowResize from "../../../../custom-hooks/useWindowResize";
import { useAuthFrontLock } from '../../../../context/useAuthFrontContext';
import { useScreen } from '../../../../context/useScreen';
import i18n from "../../../../utils/translations/i18n";
import { FALLBACK_LOCALE, LOCALE_MAPPING, PAGE_NAME, SCREEN_SIZE, TERMS_OF_USE_PAGE_MAPPING } from "../../../../utils/constant/constant";

type AuthFormProps = {
    isSignupForm: boolean;
    form: any;
    handleClick?: (arg?: any) => void;
    handleChange: (arg: any) => void;
    handleBlur: (arg: any) => void;
    handleSubmit: (arg: any) => void;
    showPasswordHandler: (arg: any) => void;
    option: string;
}

const AuthForm: React.FC<AuthFormProps> = ({ isSignupForm, form, handleClick, handleChange, handleBlur, handleSubmit, showPasswordHandler, option}) => {
    const { t } = i18n;
    const { screenDevice, size } = useWindowResize();
    const [tooltipPosition, setTooltipPosition] = useState<string>(calculateTooltipPos(screenDevice, size, option));

    useEffect(() => {
        setTooltipPosition(calculateTooltipPos(screenDevice, size, option))
    }, [screenDevice, size])
    
    const authFrontLock = useAuthFrontLock();
    const locales = authFrontLock.getUILocales();
    const locale = locales && locales.length > 0 ? locales[0] : FALLBACK_LOCALE;
    const ui_locale = LOCALE_MAPPING[locale];
    const screen = useScreen();

    const emailOTPSignIn = (event) => {
        event.preventDefault();
        screen.setDisplayScreen(isSignupForm?PAGE_NAME.EMAIL_OTP_SIGN_UP:PAGE_NAME.EMAIL_OTP_SIGN_IN);
    }

    const forgotPassword = (event) => {
        event.preventDefault();
        screen.setDisplayScreen(PAGE_NAME.FORGOT_PASSWORD);
    }

    const gdprMessage = isSignupForm
        ? 'By clicking the button below, you agree to, and have read, our'
        : t('form.gdprconsent');

    const termsPath = TERMS_OF_USE_PAGE_MAPPING[ui_locale] || TERMS_OF_USE_PAGE_MAPPING[FALLBACK_LOCALE];
    const termsUrl = `${process.env.REACT_APP_SITE_BASE_URL}/${ui_locale}/${termsPath}`;

    const renderGdprConsent = () => (
        <div className={Styles.gdprconsentNAsignUp}>
            {gdprMessage}&nbsp;
            <a href={termsUrl} target="_blank" rel="noopener noreferrer">{t('form.termsCondition')}</a>
        </div>
    );
    
    return (
        <form className={Styles.body}>
            {form.formFields.map(field => {
                return (<TextBox
                    key={form[field].id}
                    id={form[field].id + '_' + option}
                    className={form[field].className}
                    name={form[field].name}
                    type={form[field].type}
                    max={form[field].maxLength}
                    label={form[field].label}
                    placeholder={form[field].label}
                    onClick={handleClick}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isRequired={form[field].required}
                    value={form[field].value}
                    showPasswordHandler={showPasswordHandler}
                    showPasswordIcon={form[field].showPasswordIcon}
                    dangerWarn={form[field].dangerWarn}
                    errorMessage={form[field].errorMessage}
                ></TextBox>)
            })}
            <div className={Styles.passcodeContainer}>
                <span className={Styles.passcodeLink}>
                    <span onClick={emailOTPSignIn} className={`${Styles.link} option-a`} role="button" tabIndex={0}>
                        { isSignupForm ? t('sign_in.create_without_password'): t('sign_in.login_without_password')}
                    </span>
                    <span onClick={emailOTPSignIn} className={`${Styles.link} option-b`} role="button" tabIndex={0}>
                        { isSignupForm ? t('sign_in.create_with_otp') : t('sign_in.login_with_otp')}
                    </span>
                <Tooltip 
                    id={'auth-form-tooltip-'+option}
                    direction={screenDevice === SCREEN_SIZE.SMALL_SCREEN || screenDevice === SCREEN_SIZE.EXTRA_SMALL_SCREEN ? 'top' : 'right'} 
                    content={ isSignupForm ? 
                        t('sign_up.tooltip_text')
                        :
                        t('sign_in.tooltip_text')
                    }
                    className={`${Styles.tooltipContainer} ${tooltipPosition && Styles[tooltipPosition]}`}
                    onHover={() => setTooltipPosition(calculateTooltipPos(screenDevice, size, option))}
                />
                </span>
            </div>
            {isSignupForm && <div className={Styles.passwordPolicy}>
            <div className={Styles.policyHeader}>
                <span>
                    {t('validation.password_validation_heading')}
                </span>
            </div>
                <PasswordPolicy className={Styles.policyContainer} password={form['password']?.value} />
            </div>}
            {isSignupForm && locales && locales.includes('en-US') && (
                <>
                    <div className={Styles.gdprconsentNAsignUp}>
                        Registering your email address with us enables us to send you special offers from pens.com, as well as news about products and services and your designs in progress. You can change your contact preferences and unsubscribe from receiving offers at any time in the "My Account" section or by using the unsubscribe link at the bottom of our emails.
                    </div>
                    {renderGdprConsent()}
                </>
            )}
            {!isSignupForm && renderGdprConsent()}
            <Button id="auth-button" type="submit" onClick={handleSubmit} subClass="btn--pill" className={Styles.submitButton}>
                {isSignupForm ? t('sign_up.create_an_account') : t('sign_in.title')}
            </Button>
            {!isSignupForm && <div>
                <span onClick={forgotPassword} className={Styles.link}  role="button" tabIndex={0}>{t('sign_in.forgot_password')}</span>
            </div>}
        </form>
    );
};

export default AuthForm;

const calculateTooltipPos = (screenDevice: string, size: { height: number, width: number }, option: string) => {
    let position = '';
    if(screenDevice === SCREEN_SIZE.EXTRA_SMALL_SCREEN){
        const tooltip = document.getElementById(`auth-form-tooltip-${option}`);
        const tooltipIconWidth = 24;
        const tooltipMessageWidth = 205;
        // Minspace on left and right side of tooltip for top center position
        const minSpace = (tooltipMessageWidth - tooltipIconWidth)/2 + 8;
        
        const { left = minSpace } = tooltip?.getBoundingClientRect() || {};
        const right = size.width - left - tooltipIconWidth;
        
        // Choose direction based on available space
        if(right < minSpace) {
            // Move the message towards left using css class
            let ratio = Math.floor(right/tooltipMessageWidth * 10);
            position = `right${ratio}`;
        } else if(left < minSpace) {
            // Move the message towards right using css class
            let ratio = 10 - Math.floor(left/tooltipMessageWidth * 10);
            position = `right${ratio}`;
        } else {
            position = 'right5';
        }
    }
    return position;
} 
